import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useFieldRole } from '../../hooks/useFieldRole';
import { hasAuditRole } from '../../lib/jwtRoles';
import ReadingPage from './ReadingPage';
import PendingPage from './PendingPage';
import MySubmissionsPage from './MySubmissionsPage';
import CommissionPage from './CommissionPage';
import UploadsPage from './UploadsPage';
import OnboardRoutes from './onboard/OnboardPages';

export default function FieldTools() {
  const { isReader, isAdmin, hasAny } = useFieldRole();
  // Uploads shows every sender's files across realms: billing:audit only,
  // the same gate as Audit Logs (billing-api enforces it either way).
  const auth = useAuth();
  const canAudit = hasAuditRole(auth.user?.access_token);

  if (!hasAny) {
    return (
      <div className="p-4 lg:p-8">
        {/* Not wrapped in <Card>: this container is rounded-xl + shadow (Card
            is rounded-lg with no shadow) — same modal/one-off-container
            precedent groups B/D/E used elsewhere, kept as a plain
            bg-surface-raised div rather than let Card silently override the
            intentional radius. */}
        <div className="max-w-2xl mx-auto bg-surface-raised rounded-xl shadow p-8 text-center">
          <h1 className="text-xl font-semibold text-content mb-2">Field Tools</h1>
          {/* Promoted to text-content (not text-content-muted): this is the
              access-denied explanation telling the technician exactly what
              role to ask for — the only content on this screen, and the
              thing they must read to get unblocked. */}
          <p className="text-content">
            You don't have access to Field Tools. Ask an administrator to assign you the
            <code className="mx-1 px-1.5 py-0.5 bg-border-token rounded text-sm">field:reader</code>
            or
            <code className="mx-1 px-1.5 py-0.5 bg-border-token rounded text-sm">field:admin</code>
            role.
          </p>
        </div>
      </div>
    );
  }

  const tabs: Array<{ to: string; label: string; visible: boolean }> = [
    { to: 'reading', label: 'Capture Reading', visible: isReader },
    { to: 'pending', label: 'Pending Approvals', visible: isReader },
    { to: 'mine', label: 'My Submissions', visible: isReader },
    { to: 'commission', label: 'Commission Meter', visible: isAdmin },
    { to: 'onboard', label: 'Onboard Client', visible: isAdmin },
    { to: 'uploads', label: 'Uploads', visible: canAudit },
  ];

  return (
    <div className="p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-content">Field Tools</h1>
            <p className="text-sm text-content-muted mt-1">
              NFC + OCR meter capture {isAdmin ? '(admin: commission new meters and approve readings)' : '(reader: submit readings for approval)'}
            </p>
          </div>
        </div>

        <nav className="border-b border-border-token mb-6">
          {/* `flex-wrap`, not a horizontal scroller.
              -----------------------------------------------------------------
              The four tab labels are long ("Pending Approvals", "Commission
              Meter"). Measured against the real fonts this app resolves
              system-ui/monospace to, the row needs 617px at max-content and
              still 431px at min-content (each `px-4` item floored by its
              widest word), against a 327px content box at 375px
              (375 - 2*24 from the page's `p-6`). Plain `flex` is
              `flex-nowrap`, so the row overflowed by 104px with nothing to
              contain it — and because AppShell's <main> is `overflow-auto`,
              that overflow surfaced as a *page-level* sideways scroll whose
              scrollbar is an invisible overlay on touch. Hence the report:
              "a scroll to the right without a scroll bar", with "Commission
              Meter" clipped mid-word and unreachable.

              Wrapping is chosen over `overflow-x-auto` for three reasons:
              (1) every tab stays on screen, so the active tab can never be
              scrolled out of view and needs no scroll-into-view effect;
              (2) `overflow-x-auto` cannot go on this <nav> — it owns the
              `border-b` that each link's `-mb-px` underline merges into, and
              a scroll container clips at its padding box, so the active
              underline would be cut; and (3) it matches the in-repo
              precedent for this exact control (Insights.tsx's template tabs
              are `flex flex-wrap gap-2 border-b`).

              1440px is untouched: the row's 617px max-content fits the
              1136px content box there in a single line, so `flex-wrap` never
              engages. At 375px it lands as two rows of two. */}
          <ul className="flex flex-wrap gap-1">
            {tabs.filter(t => t.visible).map(tab => (
              <li key={tab.to}>
                <NavLink
                  to={tab.to}
                  // `tap-target lg:min-h-0` replaces `inline-block`: these are
                  // the screen's primary navigation controls and sat at 36px
                  // (py-2 + a 20px line box), under the 44px touch floor.
                  // `inline-block` has to GO rather than sit alongside — a
                  // display utility outranks the components-layer `.tap-target`
                  // and would strip the flex centring that makes the taller box
                  // a real target (tailwind.config.js:96). `lg:min-h-0` hands
                  // the 36px density back at desktop, so 1440px is unchanged.
                  className={({ isActive }) =>
                    `tap-target lg:min-h-0 px-4 py-2 -mb-px border-b-2 text-sm font-medium ${
                      isActive
                        // border-primary-600 kept literal: a 2px underline is a
                        // non-text UI indicator (WCAG 1.4.11 3:1, not 4.5:1) and
                        // it clears 3.20:1 against the dark page background —
                        // no fix needed. text-primary-700 does NOT clear text
                        // AA in dark (2.31:1 against #1c1917, computed by hand)
                        // so it keeps its light-mode value and gains a
                        // dark:text-accent override (teal-400, 9.39:1) rather
                        // than being replaced outright — zero light-mode change.
                        ? 'border-primary-600 text-primary-700 dark:text-accent'
                        : 'border-transparent text-content-muted hover:text-content hover:border-border-token'
                    }`
                  }
                >
                  {tab.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <Routes>
          <Route index element={<Navigate to="reading" replace />} />
          <Route path="reading" element={<ReadingPage />} />
          <Route path="pending" element={<PendingPage />} />
          <Route path="mine" element={<MySubmissionsPage />} />
          {isAdmin && <Route path="commission" element={<CommissionPage />} />}
          {isAdmin && <Route path="onboard/*" element={<OnboardRoutes />} />}
          {canAudit && <Route path="uploads" element={<UploadsPage />} />}
        </Routes>
      </div>
    </div>
  );
}
